import {MinusIcon, PlusIcon} from '@heroicons/react/solid';
import React, {useState} from 'react';

import {Button, Table, TBody, Td, Th, Thead, Tr} from '@/components';
import {useCountryList} from '@/context/CountryListContext';
import type {
  AfriexBusinessUser,
  AfriexMerchant,
} from '@/types/AdminBusinessUser';
import type {AfriexBusiness} from '@/types/AfriexBusiness';
import {mapCountryCodeToFlags} from '@/utils/countries';
import * as formatDate from '@/utils/date';

import MerchantUserSummary from './MerchantUserSummary';

type ExtraRowArg = AfriexBusinessUser;

const MerchantUserTable = ({
  merchants,
  extraRow,
  iskyckybScreen = false,
}: {
  merchants: AfriexMerchant[];
  extraRow?: (user?: ExtraRowArg) => React.ReactNode;
  iskyckybScreen?: boolean;
}) => {
  return (
    <Table data-cy="merchant-users-table">
      <Thead>
        <Tr enableHover={false}>
          <Th className="w-20 px-4">&nbsp;</Th>
          <Th className="px-4">Name</Th>
          <Th className="px-4">Email</Th>
          <Th className="px-4">Country</Th>
          <Th className="px-4">Date</Th>
        </Tr>
      </Thead>

      <TBody>
        {merchants.map((user, index) => (
          <MerchantUserTableRow
            key={index}
            user={user.person}
            business={user.business}
            extraRow={extraRow}
            index={index}
            iskyckybScreen={iskyckybScreen}
          />
        ))}
      </TBody>
    </Table>
  );
};

const MerchantUserTableRow = ({
  user,
  business,
  extraRow,
  index,
  iskyckybScreen,
}: {
  user: AfriexBusinessUser;
  business: AfriexBusiness;
  extraRow?: (user?: AfriexBusinessUser) => React.ReactNode;
  index: number;
  iskyckybScreen?: boolean;
}) => {
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';
  const country = user?.countryCode?.toUpperCase() ?? '';
  const email = user?.email ?? null;
  const flag = country ? mapCountryCodeToFlags(countryList)[country] : null;

  return (
    <>
      <Tr
        className={zebraRow}
        data-cy="merchant-user-row"
        data-user-id={user.id}
      >
        <Td isNumeric className="px-4">
          <Button
            onClick={() => setIsExpanded(!isExpanded)}
            variant="unStyled"
            data-cy="merchant-user-row-expand"
          >
            <div
              className={`border-2 border-gray-700 dark:border-dark-borderLight dark:text-gray-100 text-gray-700 w-5 h-5 flex justify-center rounded hover:bg-cyan-50 ${
                isExpanded && 'bg-cyan-50'
              }`}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4" />
              ) : (
                <PlusIcon className="w-4 h-4" />
              )}
            </div>
          </Button>
        </Td>

        <Td
          className="font-medium dark:text-gray-200 px-4"
          data-cy="merchant-user-row-name"
        >
          {user.name?.fullName}
        </Td>
        <Td className="px-4" data-cy="merchant-user-row-email">
          {email || '-'}
        </Td>
        <Td className="px-4" data-cy="merchant-user-row-country">
          {country || '-'} {flag || ''}
        </Td>
        <Td className="px-4" data-cy="merchant-user-row-date">
          {formatDate.DDMonYYYY(user.createdAt?.toString() || '')}
        </Td>
      </Tr>

      {isExpanded && (
        <Tr className="pr-5" enableHover={false}>
          <Td colspan={9} className="p-0 m-0">
            <div className="flex py-5 sm:py-10 px-8 justify-center bg-sky-50 dark:bg-transparent w-full">
              <MerchantUserSummary
                user={user}
                extraRow={extraRow ? extraRow(user) : null}
                iskyckybScreen={iskyckybScreen}
                business={business}
              />
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

export default MerchantUserTable;
